import React, { ReactElement, useEffect, useState } from 'react';
import { Modal, Checkbox, Button, Radio, Popover, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import ColumnCustomizerDrawer from './ColumnCustomizerDrawer';
import { RootState } from '../../../store';
import {
  emptyFunction,
  isValidRole,
  validateFollowUpEmails,
} from '../../../utils/utility';
import {
  exportReportData,
  fetchReportingConfig,
  setIsOnRequestReport,
} from '../../../store/actions/reportingAndAnalyticsActions';
import { downloadReconciliationFile } from '../../../store/actions/reconciliationAction';
import { ReactComponent as TransactionReportShare } from '../../../assets/images/svg/TransactionReportShare.svg';
import { ReactComponent as TransactionReportDownload } from '../../../assets/images/svg/TransactionReportDownload.svg';
import { ReactComponent as TransactionReportSchedule } from '../../../assets/images/svg/TransactionReportScheduler.svg';
import { ReactComponent as CrossCircleBlue } from '../../../assets/images/svg/cross_circle_blue.svg';
import '../ReportingAndAnalytics.less';
import { saveInPatientConfig } from '../../../store/actions/inPatientAction';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import {
  buildReportPayload,
  buildUpdatedFilters,
  getDateRangeWithOffset,
  splitColumnsByVisibility,
  convertTo12Hour,
  parseReportingDisplayDate,
  renderCollapseMenu,
} from '../../../utils/reportingUtility';
import {
  REPORTING_BUTTON_TEXT,
  REPORTING_COLUMN_CONFIG_MAP,
  REPORTING_CONSTANTS,
  REPORT_ROUTE_MAP,
  REPORT_ROUTE_MAP_CONFIG,
} from './reportingAndAnalyticsConstants';
import { ReactComponent as CalendarIcon } from '../../../../src/assets/images/svg/CalendarIcon.svg';
import { ReactComponent as RangePickerArrow } from '../../../assets/images/svg/RangePickerArrow.svg';
import { endOfDay, format, isValid, startOfDay } from 'date-fns';
import ReportingDatePicker from '../../../shared/components/crmFormElement/FormInputDate/ReportingDatePicker';
import {
  COMMON_ERROR,
  ISO_8601_EXTENDED_FORMAT,
} from '../../../shared/constants/commonConstants';
import { RadioChangeEvent } from 'antd/lib/radio';
import { useHistory } from 'react-router-dom';

const TransactionReportModal = ({
  configuredPayers,
  availableStatus,
}: any): ReactElement => {
  const dispatch = useDispatch();
  const {
    transactionReportFilters,
    reportingConfig = {},
    isRequestReportModalVisible,
    reportingDateRange,
  } = useSelector((state: RootState): any => state.reportingAndAnalytics);
  const appDetail = useSelector(
    (state: RootState): ApplicationDetailModel => state.applicationDetail,
  );
  const { location: { pathname } } = useHistory();
  const tabName = REPORT_ROUTE_MAP[pathname];

  const userDetails = appDetail?.getUserDetail();
  const { selectedColumns: initialSelectedColumns = [] } =
    splitColumnsByVisibility(userDetails?.properties?.[REPORTING_COLUMN_CONFIG_MAP[tabName]] ?? []);
  const { schedulerFilters, selectedSchedulerConfig = [] } = reportingConfig;
  const [visible, setVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentModalKey, setCurrentModalKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [selectedFileFormat, setSelectedFileFormat] = useState<string>(
    REPORTING_CONSTANTS.XLSX,
  );
  const [scheduleOptions, setScheduleOptions] = useState<string[]>([]);
  const [selectedDailyTime, setSelectedDailyTime] = useState<string>('');
  const [activeKey, setActiveKey] = useState<string | string[]>('');
  const [, setSaveConfig] = useState(false);
  const [selectedOnRequestFilter, setSelectedOnRequestFilter] =
    useState<string>('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const reportingModuleWriteAccess = isValidRole(
    REPORTING_CONSTANTS.REPORTING_WRITE_ACCESS,
  );
  const [onRequestDateRange, setOnRequestDateRange] = useState<any>({});
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const REPORT_MODAL_CONFIG: Record<
    string,
    {
      getEmailList: (reportingConfig: any) => any[];
      extraLogic?: () => void;
    }
  > = {
    scheduleReport: {
      getEmailList: ({ schedulerEmail }: any): any[] => schedulerEmail || [],
      extraLogic: (): void => {
        const activeTypes: any = Array?.from(
          new Set(
            selectedSchedulerConfig
              ?.filter((config: any): boolean => config?.isEnabled)
              ?.map((config: any): string => config?.filterType),
          ),
        );
        setScheduleOptions(activeTypes);

        const enabledDaily = selectedSchedulerConfig?.find(
          (config: any): boolean =>
            config?.filterType === REPORTING_CONSTANTS.DAILY &&
            config?.isEnabled,
        );
        const enabledRange = enabledDaily?.timeFilter
          ? `${convertTo12Hour(
            enabledDaily?.timeFilter?.startTime,
          )} - ${convertTo12Hour(enabledDaily?.timeFilter?.endTime)}`
          : '';
        setSelectedDailyTime(enabledRange);
      },
    },
    sendAsMail: {
      getEmailList: ({ reportingEmail }: any): any[] => reportingEmail || [],
    },
    onRequestReport: {
      getEmailList: ({ onRequestReportingEmail }: any): any[] =>
        onRequestReportingEmail || [],
    },
  };

  useEffect((): void => {
    if (reportingModuleWriteAccess) {
      dispatch(fetchReportingConfig({ tabName }));
    }
  }, []);

  useEffect((): void => {
    if (!reportingConfig || !currentModalKey) {
      return;
    }

    const modalConfig = REPORT_MODAL_CONFIG[currentModalKey];
    if (!modalConfig) {
      return;
    }

    setEmailList(modalConfig.getEmailList(reportingConfig));

    if (modalConfig.extraLogic) {
      modalConfig.extraLogic();
    }
  }, [currentModalKey, reportingConfig, selectedSchedulerConfig]);

  const formattedstartDate =
    startDate && isValid(startDate)
      ? format(startDate, REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT)
      : '';

  const formattedendDate =
    endDate && isValid(endDate)
      ? format(endDate, REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT)
      : '';
  const handleDateRangeSelect = (startStr: string, endStr: string): void => {
    const start = parseReportingDisplayDate(startStr);
    const end = parseReportingDisplayDate(endStr);
    setStartDate(start);
    setEndDate(end);
    setOnRequestDateRange({
      startDate: start
        ? format(startOfDay(start), ISO_8601_EXTENDED_FORMAT)
        : '',
      endDate: end ? format(endOfDay(end), ISO_8601_EXTENDED_FORMAT) : '',
    });
  };

  const handleDateOk = (): void => {
    setSelectedOnRequestFilter('Custom Date Selection');
    setIsDatePickerOpen(false);
  };
  const openModal = (key: string): void => {
    setCurrentModalKey(key);
    setVisible(true);
  };

  const handleCancel = (): void => {
    dispatch(setIsOnRequestReport(false));
    setVisible(false);
    setCurrentModalKey(null);
    setIsDatePickerOpen(false);
    setEmailList([]);
    setScheduleOptions([]);
    setEmailInput('');
    setSelectedOnRequestFilter('');
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const handleAddEmail = (): void => {
    const trimmed = emailInput.trim();
    if (
      trimmed &&
      validateFollowUpEmails([trimmed], emailList) &&
      !emailList?.includes(trimmed)
    ) {
      setEmailList([...emailList, trimmed]);
      setEmailInput('');
    }
  };

  useEffect((): void => {
    if (isRequestReportModalVisible) {
      openModal(REPORTING_CONSTANTS.ON_REQUEST_REPORT_KEY);
    }
  }, [isRequestReportModalVisible]);

  const handleConfirm = (): void => {
    setIsSubmitting(true);
    const requestPayload = buildReportPayload({
      emailList,
      currentModalKey,
      reportingConfig,
      selectedOnRequestFilter,
      scheduleOptions,
      selectedDailyTime,
      onRequestDateRange,
      tabName,
    });
    const callBack = (isReqSuccess: boolean, message: string): void => {
      setIsSubmitting(false);
      if (isReqSuccess) {
        setSuccessMessage(message || 'Success');
        dispatch(fetchReportingConfig({ tabName }));
        setIsSuccess(true);
      } else {
        setSuccessMessage(message || COMMON_ERROR);
        setIsSuccess(false);
      }
      setTimeout((): void => {
        setVisible(false);
        setSuccessMessage('');
      }, 2000);
    };
    dispatch(setIsOnRequestReport(false));
    dispatch(exportReportData({ requestPayload, callBack }));
  };

  const handleDownloadClicked = (): void => {
    const isTATDailyReport =
      (tabName === REPORTING_CONSTANTS.TRANSACTION_TAT) &&
      (transactionReportFilters.dateFilter === REPORTING_CONSTANTS.ZERO_STRING);
    const dateRangeConfig = isTATDailyReport ?
     REPORTING_CONSTANTS.TAT_DAILY : transactionReportFilters?.dateFilter;
    const { startDate: rangePickerStartDate, endDate: rangePickerEndDate } =
      getDateRangeWithOffset(dateRangeConfig);
    const filters = Object?.fromEntries(
      buildUpdatedFilters(
        transactionReportFilters,
        configuredPayers,
        availableStatus,
      )?.map(({ key, value }: any): any => [key, value]),
    );
    const dateRangeSelected = !isEmpty(reportingDateRange);

    const payload = {
      ClaimStatusList: filters.claimStatus,
      payerIds: filters?.payer,
      tabName,
      ColumnsToExport: initialSelectedColumns,
      downloadTypeId: 1,
      fileType:
        selectedFileFormat === REPORTING_CONSTANTS.CSV
          ? selectedFileFormat
          : REPORTING_CONSTANTS.EXCEL,
      startDate: dateRangeSelected
        ? reportingDateRange?.startDate
        : rangePickerStartDate,
      endDate: dateRangeSelected
        ? reportingDateRange?.endDate
        : rangePickerEndDate,
    };
    const downloadSuccessful = (): void => {
      setSuccessMessage(REPORTING_CONSTANTS.DOWNLOAD_SUCCESSFUL);
      setTimeout((): void => {
        setSuccessMessage('');
        setVisible(false);
        handleCancel();
      }, 2000);
    };
    setDrawerVisible(false);
    setVisible(false);

    dispatch(
      downloadReconciliationFile({
        payload,
        extension: selectedFileFormat,
        url: REPORTING_CONSTANTS.DOWNLOAD_URL,
        onSuccess: (): any => downloadSuccessful,
        onError: (): any => emptyFunction,
      }),
    );
  };

  const renderConfirmButton = (isDisabled: boolean): ReactElement => {
    if (isSubmitting) {
      return (
        <div className="loading-container">
          <Spin tip="Loading..." className="custom-spin" />
        </div>
      );
    }

    if (successMessage) {
      return <></>;
    }

    return (
      <Button
        className="download-btn center"
        onClick={handleConfirm}
        disabled={isDisabled}
      >
        {REPORTING_BUTTON_TEXT.CONFIRM}
      </Button>
    );
  };

  const renderEmailInput = (label: string, subLabel?: string): ReactElement => (
    <div>
      <p className="modal-txt">{label}</p>
      {subLabel && <p className="modal-txt">{subLabel}</p>}
      <span className="email-hint">{REPORTING_CONSTANTS.ENTER_EMAIL_TEXT}</span>
      <input
        type="email"
        value={emailInput}
        placeholder={REPORTING_CONSTANTS.ENTER_EMAIL}
        onChange={(e: any): void => setEmailInput(e?.target?.value)}
        onKeyDown={(e: any): void => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
          }
        }}
        className="customEmailInput"
      />
      <div className="email-tags-wrapper">
        {emailList?.map(
          (email: string, index: number): ReactElement => (
            <div key={index} className="email-tag">
              {email}
              <CrossCircleBlue
                className="cross-icon mouse-pointer email-tag-cancel"
                onClick={(): void =>
                  setEmailList(
                    emailList?.filter((e: string): boolean => e !== email),
                  )
                }
              />
            </div>
          ),
        )}
      </div>
    </div>
  );

  const renderScheduleOptions = (): ReactElement => {
    const toggleSchedule = (type: string): void =>
      setScheduleOptions((prev: string[]): string[] =>
        prev?.includes(type)
          ? prev?.filter((el: string): boolean => el !== type)
          : [...prev, type],
      );

    const groupedConfig = schedulerFilters?.reduce(
      (acc: any, curr: any): Record<string, any[]> => {
        const key = curr?.filterType;
        acc[key] = acc[key] || [];
        acc[key]?.push(curr);
        return acc;
      },
      {} as Record<string, typeof schedulerFilters>,
    );

    const renderDailyTimeSelection = (): ReactElement | null => {
      const dailyConfigs = groupedConfig[REPORTING_CONSTANTS.DAILY]?.[0];
      if (
        !scheduleOptions?.includes(REPORTING_CONSTANTS.DAILY) ||
        isEmpty(dailyConfigs)
      ) {
        return null;
      }
      return (
        <div className="daily-container">
          <div className="modal-sub-text mt-0">
            {REPORTING_CONSTANTS.DAILY_TIME_RANGE_PROMPT}
          </div>
          <Radio.Group
            onChange={(e: any): void => setSelectedDailyTime(e.target.value)}
            value={selectedDailyTime}
            className={'reportingRadioSelect'}
          >
            {dailyConfigs?.timeFilter?.map(
              (cfg: any, idx: number): ReactElement => {
                const timeLabel = cfg;
                return (
                  <Radio className="pr-10" key={idx} value={timeLabel}>
                    {timeLabel}
                  </Radio>
                );
              },
            )}
          </Radio.Group>
          <p className="modal-txt font-italic">
            {REPORTING_CONSTANTS.DAILY_TIME_RANGE_FOOTER}
          </p>
        </div>
      );
    };

    return (
      <div className="schedule-options">
        <p className="modal-sub-text">{REPORTING_CONSTANTS.SCHEDULE_PROMPT}</p>
        <p className="modal-txt">{REPORTING_CONSTANTS.SCHEDULE_FOOTER}</p>
        <div className="scheduler-selector-container">
          {Object?.keys(groupedConfig)?.map(
            (filterType: string): ReactElement => (
              <div key={filterType}>
                <Checkbox
                  checked={scheduleOptions?.includes(filterType)}
                  onChange={(): void => toggleSchedule(filterType)}
                  className={`${scheduleOptions?.includes(filterType)
                      ? 'custom-checkbox-selected'
                      : 'custom-checkbox-unselected'
                    } customCheckBox`}
                >
                  {filterType}
                </Checkbox>
              </div>
            ),
          )}
        </div>

        {renderDailyTimeSelection()}
      </div>
    );
  };

  const renderModalContent = (): ReactElement => {
    switch (currentModalKey) {
      case 'downloadReport':
        return (
          <>
            <p className="modal-txt">
              {REPORTING_CONSTANTS.DOWNLOAD_REPORT_PROMPT}
            </p>
            <div className="modal-button-group">
              <button
                className="customise-btn"
                onClick={(): void => {
                  setDrawerVisible(true);
                  setVisible(false);
                }}
              >
                {REPORTING_BUTTON_TEXT.CUSTOMISE}
              </button>
              <button className="download-btn" onClick={handleDownloadClicked}>
                {REPORTING_BUTTON_TEXT.DOWNLOAD}
              </button>
            </div>
          </>
        );
      case 'sendAsMail':
        return (
          <>
            <p className="modal-txt">
              {REPORTING_CONSTANTS.SHARE_REPORT_PROMPT}
            </p>
            {renderEmailInput(REPORTING_CONSTANTS.SHARE_REPORT_TEXT, '')}
            {renderConfirmButton(!isEmpty(emailInput))}
          </>
        );
      case 'scheduleReport':
        return (
          <>
            {renderEmailInput(REPORTING_CONSTANTS.GET_REPORTS_DELIVERED, '')}
            {renderScheduleOptions()}

            {renderCollapseMenu(activeKey, setActiveKey)}
            {renderConfirmButton(!isEmpty(emailInput))}
          </>
        );
      case 'onRequestReport':
        return (
          <>
            {renderEmailInput(
              REPORTING_CONSTANTS.REQUEST_FOR_OLD_REPORT,
              REPORTING_CONSTANTS.REQUEST_ENTER_EMAIL,
            )}
            <div className="modal-range-group">
              <p className="modal-sub-text">
                {REPORTING_CONSTANTS.REQUEST_SELECT_TIME_RANGE}
              </p>
              <p className="modal-txt">
                {REPORTING_CONSTANTS.REQUEST_DOWNLOAD_FOOTER}
              </p>

              <Radio.Group
                onChange={(e: RadioChangeEvent): void => {
                  const value = e?.target.value;
                  if (value === 'Custom Date Selection') {
                    setIsDatePickerOpen(true);
                  } else {
                    setStartDate(undefined);
                    setEndDate(undefined);
                    setSelectedOnRequestFilter(value);
                    setIsDatePickerOpen(false);
                  }
                }}
                value={selectedOnRequestFilter}
                className="reportingRadioSelect"
              >
                {(reportingConfig?.onRequestFilters || [])?.map(
                  (field: string, idx: number): ReactElement => (
                    <Radio
                      className="pr-10 display_inline_flex"
                      key={`${field}-${idx}`}
                      value={field}
                    >
                      {field === 'Custom Date Selection' ? (
                        <div className="compact-range-wrapper mt--7">
                          <div
                            className="range-input"
                            onClick={(
                              e: React.MouseEvent<HTMLDivElement>,
                            ): void => {
                              e?.stopPropagation();
                              setIsDatePickerOpen(!isDatePickerOpen);
                            }}
                          >
                            <div className="field">
                              {formattedstartDate || (
                                <span className="placeholder">
                                  {REPORTING_CONSTANTS.REPORTING_PLACEHOLDER}
                                </span>
                              )}
                            </div>
                            <div className="arrow">
                              <RangePickerArrow />
                            </div>
                            <div className="field">
                              {formattedendDate || (
                                <span className="placeholder">
                                  {REPORTING_CONSTANTS.REPORTING_PLACEHOLDER}
                                </span>
                              )}
                            </div>
                            <CalendarIcon />
                          </div>
                          {isDatePickerOpen && (
                            <div className="onRequestCalander">
                              <ReportingDatePicker
                                initialStartDate={startDate}
                                initialEndDate={endDate}
                                limit={366} // 1 year limit
                                leftLimit={366} // 1 year limit
                                rightLimit={30}
                                disablefutureDates={true}
                                disabledStartDate={false}
                                setIsDatePickerOpen={setIsDatePickerOpen}
                                handleDateOk={handleDateOk}
                                setSelect={handleDateRangeSelect}
                                confirmBtnName={'Apply'}
                                isModalCalander={true}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        field
                      )}
                    </Radio>
                  ),
                )}
              </Radio.Group>
            </div>
            {renderConfirmButton(
              !isEmpty(emailInput) || isEmpty(selectedOnRequestFilter),
            )}
          </>
        );

      default:
        return <></>;
    }
  };

  return (
    <div>
      <div className="icon-row">
        <Popover
          content={
            <span className="inpatient_tooltip_header">
              {REPORTING_CONSTANTS.DOWNLOAD_TRANSACTION_REPORT}
            </span>
          }
          placement="bottom"
        >
          <TransactionReportDownload
            onClick={(): void => openModal('downloadReport')}
          />
        </Popover>
        {reportingModuleWriteAccess && (
          <>
            <Popover
              content={
                <span className="inpatient_tooltip_header">
                  {REPORTING_CONSTANTS.SHARE_REPORT}
                </span>
              }
              placement="bottom"
            >
              <TransactionReportShare
                className="mouse-pointer"
                onClick={(): void => openModal('sendAsMail')}
              />
            </Popover>

            <Popover
              content={
                <span className="inpatient_tooltip_header">
                  {REPORTING_CONSTANTS.SCHEDULE_REPORT}
                </span>
              }
              placement="bottom"
            >
              <TransactionReportSchedule
                onClick={(): void =>
                  openModal(REPORTING_CONSTANTS.SCHEDULE_REPORT_KEY)
                }
              />
            </Popover>
          </>
        )}
      </div>

      <Modal
        title={
          currentModalKey === REPORTING_CONSTANTS.ON_REQUEST_REPORT_KEY
            ? REPORTING_CONSTANTS.ON_REQUEST_REPORT
            : currentModalKey === REPORTING_CONSTANTS.DOWNLOAD_REPORT_KEY
              ? REPORTING_CONSTANTS.DOWNLOAD_REPORT
              : currentModalKey === REPORTING_CONSTANTS.SEND_AS_MAIL_KEY
                ? REPORTING_CONSTANTS.SEND_REPORT_AS_MAIL
                : REPORTING_CONSTANTS.SCHEDULE_REPORT
        }
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        width={
          currentModalKey === REPORTING_CONSTANTS.ON_REQUEST_REPORT_KEY
            ? 662
            : 632
        }
        className="config-modal"
      >
        <div className="modal-divider" />
        {renderModalContent()}
        {successMessage && (
          <div className={`response-msg ${isSuccess ? 'success' : 'failure'}`}>
            {successMessage}
          </div>
        )}
      </Modal>

      <ColumnCustomizerDrawer
        visible={drawerVisible}
        handleDownloadClicked={handleDownloadClicked}
        onClose={(): void => {
          setDrawerVisible(false);
        }}
        setSelectedFileFormat={setSelectedFileFormat}
        onSave={(columns: any): void => {
          dispatch(
            saveInPatientConfig(
              columns,
              setSaveConfig,
              REPORT_ROUTE_MAP_CONFIG[tabName],
              true,
            ),
          );
        }}
      />
    </div>
  );
};

export default TransactionReportModal;
